import React, { useCallback, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { FormattedDate } from 'react-intl';
import Slider, { Settings } from 'react-slick';
import { Button } from 'hfs-common-ui';
import FormattedMessageWrapper from '~/components/views/FormattedMessageWrapper/FormattedMessageWrapper';
import type { ChangeLogItem } from '../../store/ChangeLogState';
import Icon from '~uikit/Icon/Icon';
import Label from '~uikit/text/Label/Label';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import style from './ChangeLogItemRow.module.pcss';
import SliderPageLegend from './SliderPageLegend';
import * as S from './styled';

const sliderSettings: Settings = {
  dots: true,
  dotsClass: 'slick-dots sliderDot',
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  lazyLoad: 'progressive',
};

export interface Props {
  item: ChangeLogItem;
}

const ChangeLogItemRow = ({ item }: Props) => {
  const [viewMore, setViewMore] = useState(false);
  const [page, setPage] = useState(0);
  const sliderRef = useRef<Slider>(null);
  const contentItems = viewMore ? item.content : item.content.slice(0, 4);
  const hasMore = contentItems.length < item.content.length && !viewMore;

  const onLeftClick = useCallback(() => {
    sliderRef.current?.slickPrev();
  }, []);

  const onRightClick = useCallback(() => {
    sliderRef.current?.slickNext();
  }, []);

  return (
    <article className={style.ChangeLogItemRow} data-testid="ChangeLogItemRow">
      <div className={style.title}>
        <Label>
          <FormattedDate value={item.date} format="longMonth" />
        </Label>
        <h4>
          <FormattedMessageWrapper langKey={item.title} />
        </h4>
      </div>
      <div className={style.content}>
        {contentItems.map((row, index) => (
          <S.ContentRowDiv key={row} $fadeOut={hasMore && contentItems.length - 1 === index}>
            <Trans i18nKey={row}></Trans>
          </S.ContentRowDiv>
        ))}
      </div>
      {hasMore && (
        <S.ContentReadMoreBtnDiv>
          <Button
            customStyle={{ color: 'primary', backgroundColor: 'surface' }}
            type="button"
            size="small"
            onClick={() => setViewMore(true)}
          >
            <FormattedMessageWrapper langKey="web.dealer_access.read_more_link" />
          </Button>
        </S.ContentReadMoreBtnDiv>
      )}
      {item.images.length > 0 && (
        <div className={style.imageContent}>
          {item.images.length > 1 && (
            <div className={style.arrows}>
              <Icon icon="arrow-left" size="24" onClick={onLeftClick} />
              <Icon icon="arrow-right" size="24" onClick={onRightClick} />
            </div>
          )}
          <Slider
            {...sliderSettings}
            ref={sliderRef}
            className={style.slider}
            afterChange={setPage}
            beforeChange={setPage}
          >
            {item.images.map((imageUrl) => (
              <img className={style.image} key={imageUrl} src={imageUrl} alt="Changelog image" />
            ))}
          </Slider>
          <SliderPageLegend item={item} imageNumber={page} />
        </div>
      )}
    </article>
  );
};

export default ChangeLogItemRow;
